import { useMemo } from "react";
import { formatPropertyPrice } from "../../../utils/propertyDetailFormat";

function buildChartPoints(events = [], currency = "COP") {
  const sorted = [...events]
    .filter((event) => event.new_price_min != null || event.newPriceMin != null)
    .sort(
      (left, right) =>
        new Date(left.changed_at || left.changedAt) -
        new Date(right.changed_at || right.changedAt)
    );

  return sorted.map((event) => ({
    date: new Date(event.changed_at || event.changedAt),
    price: event.new_price_min ?? event.newPriceMin,
    label: (event.change_type || event.changeType || "").replace(/_/g, " ").toLowerCase(),
    currency: event.currency || currency,
  }));
}

const PropertyPriceHistoryChart = ({ priceHistory }) => {
  const currency = priceHistory?.currency || "COP";
  const events = priceHistory?.events || [];
  const points = useMemo(() => buildChartPoints(events, currency), [events, currency]);

  if (!points.length) {
    return (
      <section className="property-detail-section" aria-label="Price history chart">
        <h2 className="property-detail-section__title">Price history</h2>
        <p className="property-detail-empty">
          No historical price changes have been recorded yet.
        </p>
      </section>
    );
  }

  const width = 640;
  const height = 220;
  const padding = { top: 20, right: 16, bottom: 36, left: 72 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;
  const prices = points.map((point) => point.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange = maxPrice - minPrice || 1;
  const minTime = points[0].date.getTime();
  const maxTime = points[points.length - 1].date.getTime();
  const timeRange = maxTime - minTime || 1;

  const coordinates = points.map((point) => {
    const x =
      padding.left +
      ((point.date.getTime() - minTime) / timeRange) * innerWidth;
    const y =
      padding.top +
      innerHeight -
      ((point.price - minPrice) / priceRange) * innerHeight;

    return { ...point, x, y };
  });

  const polyline = coordinates.map((point) => `${point.x},${point.y}`).join(" ");

  return (
    <section className="property-detail-section" aria-label="Price history chart">
      <h2 className="property-detail-section__title">Price history</h2>
      <div className="property-detail-chart" role="img" aria-label="Price history line chart">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="property-detail-chart__svg"
          preserveAspectRatio="xMidYMid meet"
        >
          <line
            x1={padding.left}
            y1={padding.top + innerHeight}
            x2={padding.left + innerWidth}
            y2={padding.top + innerHeight}
            className="property-detail-chart__axis"
          />
          <text x={padding.left} y={height - 8} className="property-detail-chart__label">
            {points[0].date.toLocaleDateString()}
          </text>
          <text
            x={padding.left + innerWidth}
            y={height - 8}
            textAnchor="end"
            className="property-detail-chart__label"
          >
            {points[points.length - 1].date.toLocaleDateString()}
          </text>
          <text x={8} y={padding.top + 8} className="property-detail-chart__label">
            {formatPropertyPrice(maxPrice, currency)}
          </text>
          <text x={8} y={padding.top + innerHeight} className="property-detail-chart__label">
            {formatPropertyPrice(minPrice, currency)}
          </text>
          {coordinates.length > 1 ? (
            <polyline
              points={polyline}
              fill="none"
              className="property-detail-chart__line"
              strokeWidth="2.5"
            />
          ) : null}
          {coordinates.map((point) => (
            <g key={point.date.toISOString()}>
              <circle
                cx={point.x}
                cy={point.y}
                r="5"
                className="property-detail-chart__point"
                aria-hidden="true"
              />
              <title>
                {point.date.toLocaleDateString()} — {formatPropertyPrice(point.price, point.currency)}
              </title>
            </g>
          ))}
        </svg>
      </div>
      {points.length === 1 ? (
        <p className="intel-meta">Only one price point recorded so far.</p>
      ) : null}
    </section>
  );
};

export default PropertyPriceHistoryChart;
